/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';

import {
  type DuePlan,
  type PlanReview,
  type RecordReviewInput,
  reviewsApi,
} from '../api/reviews.js';

const reviewsKey = (planId: string): readonly unknown[] => ['itps', planId, 'reviews'];
const DUE_KEY = ['itps', 'reviews', 'due'] as const;

/** A plan's clinical-review history (newest first). */
export function useReviews(planId: string): UseQueryResult<readonly PlanReview[]> {
  return useQuery({
    queryKey: reviewsKey(planId),
    queryFn: () => reviewsApi.list(planId),
    enabled: planId !== '',
  });
}

/** The caller's "reviews due" worklist (BRD M01 review reminder). */
export function useReviewsDue(): UseQueryResult<readonly DuePlan[]> {
  return useQuery({ queryKey: DUE_KEY, queryFn: () => reviewsApi.due() });
}

export function useRecordReview(
  planId: string,
): UseMutationResult<readonly PlanReview[], Error, RecordReviewInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RecordReviewInput) => reviewsApi.record(planId, payload),
    onSuccess: (rows) => {
      qc.setQueryData(reviewsKey(planId), rows);
      // The review rolls the plan's status / review date forward.
      void qc.invalidateQueries({ queryKey: ['itps', planId] });
      void qc.invalidateQueries({ queryKey: ['itps'] });
      void qc.invalidateQueries({ queryKey: DUE_KEY });
    },
  });
}
